import { useEffect, useState, useMemo, useRef } from "react"
import moment from "moment-timezone"
import Datatable from "../../Data/TablesData/Datatable"
import { useDatatableCtx } from "../../components/Datatable/DatatableContext"
import CancelIcon from "@mui/icons-material/Cancel"
import Modal from "../../components/Modal"
import {
    Autocomplete,
    Checkbox,
    Chip,
    FormControlLabel,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    Slider,
    TextField,
} from "@mui/material"
import SimpleBackdropLoader from "../BackdropLoading/BackdropLoading"
import AlertLogs from "./AlertLogs"
import { FiClock } from "react-icons/fi"
import Cascader from "rc-cascader/lib/Cascader.js"
import "../Routing/ReactCascader.css"
import { useModalCtx } from "../../providers/ModalProvider"
import { useFormik } from "formik"

export const BASE_URL = process.env.REACT_APP_API_URL
import { formatRoleName } from "../DynamicAssets/constant"
import { computeColumns, getSelectedHierarchy } from "../../utils"
import { AddAlertValidationSchema } from "../../validations/AddAlertvalidationSchema"
import { useLocalization } from "../../providers/LocalizationProvider"
import { useFormat } from "../../context/FormatContext"
import { FetchHelper } from "../../utils/fetchHelper"
import { showError, showSuccess } from "../../utils/snackbar"

const Alerts = () => {
    const editingAlertRef = useRef(null)
    const modalContext = useModalCtx()
    const { setModalStatus, setRowData, setColumnMetaData } = useDatatableCtx()
    const [isEditMode, setEditMode] = useState(false)
    const [alertsTableData, setAlertsTableData] = useState([])
    const { modalStatus: formModalStatus, modalStatusHandler: handleFormModalStatus } = modalContext
    const timezones = moment.tz.names()
    const { voiceiiTags } = useLocalization()
    const formattedTimezones = timezones.map((timezone) => {
        const offset = moment.tz(timezone).utcOffset()
        const hours = Math.floor(Math.abs(offset) / 60)
        const minutes = Math.abs(offset) % 60
        const sign = offset >= 0 ? "+" : "-"
        const gmtOffset = `(GMT${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(
            2,
            "0",
        )})`

        return {
            name: timezone,
            label: `${gmtOffset} ${timezone}`,
        }
    })
    const { formatDate } = useFormat()
    const columns = useMemo(
        () =>
            computeColumns({
                data: alertsTableData?.data,
                meta: alertsTableData?.meta,
                action: ({ row, meta }) => {
                    setRowData(row)
                    setColumnMetaData(meta)
                    setModalStatus(true)
                },
                formatDate,
            }),
        [alertsTableData?.data],
    )
    const data = useMemo(() => alertsTableData?.data, [alertsTableData?.data])

    const [zonesList, setZonesList] = useState([])
    const [usersList, setUsersList] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    //state for alert form
    const [alertName, setAlertName] = useState("")
    const [selectedUsers, setSelectedUsers] = useState([])
    const [modeOfAlert, setModeOfAlert] = useState("email")
    const [isActive, setIsActive] = useState(true)

    const [uuidForUpdateApi, setUuidForUpdateApi] = useState("")
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteData, setDeleteData] = useState("")

    const [selectedValues, setSelectedValues] = useState({})
    const [timeSlots, setTimeSlots] = useState([])
    const [selectedTime, setSelectedTime] = useState("")
    const [selectedTimezone, setSelectedTimezone] = useState({
        name: "America/Toronto",
        label: "(GMT-05:00) America/Toronto",
    })

    const [filterFields, setFilterFields] = useState({
        fill_level: [0, 100],
        temperature: [-50, 100],
        battery_level: [0, 100],
        is_active: null,
    })
    const [selectedAsset, setSelectedAsset] = useState("select-value")

    const fetchZonesData = async (assetUUID) => {
        try {
            const resData = await FetchHelper.post("api/v1/data", {
                page_name: "zones",
                asset_uuid: assetUUID,
            })

            if (resData?.data?.status) {
                const zones = resData?.data?.data || []

                setZonesList(zones)

                return zones
            }

            return []
        } catch (error) {
            console.warn(error, "Error")
            return []
        }
    }

    useEffect(() => {
        setSelectedValues({})
        setFieldValue("selectedValues", {}, true)
        setZonesList([])

        if (selectedAsset && selectedAsset !== "select-value" && selectedAsset.length > 0) {
            fetchZonesData(selectedAsset)
        }
    }, [selectedAsset])

    const fetchUsersList = async () => {
        try {
            const resData = await FetchHelper.post("api/v1/data", {
                page_name: "fleet_manager_users",
            })
            if (resData?.data?.status) {
                setUsersList(resData?.data?.data)
            }
        } catch (error) {
            console.warn(error, "Error")
        }
    }

    useEffect(() => {
        fetchUsersList()
    }, [])

    const [istableLoading, setIstableLoading] = useState(true)

    // data to show on table
    const fetchAlertsData = async () => {
        try {
            // setIsLoading(true)

            const apiData = await FetchHelper.get(
                "api/v1/alerts",
                // {
                //     page_name: "alerts",
                //     uuid: "",
                // }, // ✅ params
            )
            if (apiData?.data?.status) {
                setAlertsTableData(apiData?.data)
                setIstableLoading(false)
            } else {
                setIstableLoading(false)
            }
        } catch (error) {
            console.warn(error, "Error")
            setIstableLoading(false)
        } finally {
            setIstableLoading(false)
        }
    }

    const addAlert = async () => {
        const hasSelectedZones =
            selectedValues &&
            Object.values(selectedValues).some((arr) => Array.isArray(arr) && arr.length > 0)
        const zoneValues = hasSelectedZones ? getSelectedHierarchy(zonesList, selectedValues) : []
        let zonesObj = []

        // Add the zone filters
        Object.entries(zoneValues).forEach(([key, value]) => {
            zonesObj.push({
                value,
            })
        })

        const apiData = {
            asset_uuid: selectedAsset,
            alert_name: alertName,
            zone_value: zonesObj.length > 0 ? zoneValues : null,
            users: selectedUsers.map((d) => d.id),
            mode_of_alert: modeOfAlert,
            is_active: isActive,
            // Only include filters with a value
            filters: assetProperty
                .filter(
                    (el) =>
                        el.assetValue !== null &&
                        el.assetValue !== undefined &&
                        el.assetValue !== "",
                )
                .map((el) => ({ [el.asset_slug]: el.assetValue })),
            scheduled_at: `${moment().format("YYYY-MM-DD")} ${selectedTime.value}`,
            timezone: selectedTimezone.name,
            condition: "greater_than_equal_to",
            parameter: "fill_level",
            threshold_value: "10",
        }

        try {
            setIsLoading(true)
            const resData = await FetchHelper.post("api/v1/alerts", apiData)
            if (resData?.data?.status) {
                handleFormModalStatus(false)
                fetchAlertsData()
                resetState()
                setSelectedAsset("select-value")
                showSuccess(voiceiiTags(`${resData?.data?.message}`))
            } else {
                showError(voiceiiTags(`${resData?.data?.message}`))
                fetchAlertsData()
            }
            setIsLoading(false)
        } catch (error) {
            showError(voiceiiTags(`${error.response.data.message}`))
            console.warn(error, "Error")
            setIsLoading(false)
        }
    }

    // update api
    const updateAlert = async () => {
        const hasSelectedZones =
            selectedValues &&
            Object.values(selectedValues).some((arr) => Array.isArray(arr) && arr.length > 0)
        const zoneValues = hasSelectedZones ? getSelectedHierarchy(zonesList, selectedValues) : []
        let zonesObj = []

        // Add the zone filters
        Object.entries(zoneValues).forEach(([key, value]) => {
            zonesObj.push({
                value,
            })
        })
        const apiData = {
            asset_uuid: selectedAsset,
            alert_name: alertName,
            zone_value: zonesObj.length > 0 ? zoneValues : null,
            users: selectedUsers.map((d) => d.id),
            mode_of_alert: modeOfAlert,
            is_active: isActive,
            // Only include filters with a value
            filters: assetProperty
                .filter(
                    (el) =>
                        el.assetValue !== null &&
                        el.assetValue !== undefined &&
                        el.assetValue !== "",
                )
                .map((el) => ({ [el.asset_slug]: el.assetValue })),
            scheduled_at: `${moment().format("YYYY-MM-DD")} ${selectedTime.value}`,
            timezone: selectedTimezone.name,
            condition: "greater_than_equal_to",
            parameter: "fill_level",
            threshold_value: "10",
        }

        try {
            setIsLoading(true)
            const resData = await FetchHelper.post(`api/v1/alerts/${uuidForUpdateApi}`, apiData)
            if (resData?.data?.status) {
                fetchAlertsData()
                handleFormModalStatus(false)
                resetState()
                showSuccess(voiceiiTags(`${resData.data.message}`))
            } else {
                showError(voiceiiTags(`${resData.message}`))
            }
            setIsLoading(false)
        } catch (error) {
            console.warn(error, "Error")
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchAlertsData()
    }, [])

    const handleUserChange = (event, newValue) => {
        if (newValue.some((user) => user.id === "selectAll")) {
            if (isAllSelected) {
                setSelectedUsers([])
            } else {
                setSelectedUsers(usersList)
            }
        } else {
            setSelectedUsers(newValue)
        }
    }

    const deleteAlert = async (value) => {
        try {
            setIsLoading(true)
            const res = await FetchHelper.delete(`api/v1/alerts/${value}`)
            if (res.status) {
                fetchAlertsData()
                setDeleteModal(false)
                setDeleteData("")
                showSuccess(voiceiiTags(`${res.data.message}`))
            } else {
                showError(voiceiiTags(`${res.message}`))
            }
            setIsLoading(false)
        } catch (error) {
            console.warn(error, "Error")
            setIsLoading(false)
        }
    }

    const resetState = () => {
        setAlertName("")
        setSelectedUsers([])
        setModeOfAlert("email")
        setIsActive(true)
        setUuidForUpdateApi("")
        setSelectedTime("")
        setSelectedTimezone({
            name: "America/Toronto",
            label: "(GMT-05:00) America/Toronto",
        })
        setFilterFields({})
        setSelectedValues({})
    }

    const getSelectedOption = (value) => {
        const time = value?.split(" ")[1]
        return timeSlots.find((item) => item.value === `${time}`) || null
    }

    const getSelectedTimezoneOption = (value) => {
        return formattedTimezones.find((item) => item.name === value) || null
    }

    function parseStringToObject(inputString) {
        if (!inputString) return {}
        // Split the input string by " -> " to identify sections
        const sections = inputString?.split(" -> ")
        if (!sections || sections.length === 0) return {}
        const result = {}

        // Start with the first section as the current key
        let currentKey = sections[0]
        result[currentKey] = []

        // Iterate through the remaining sections
        for (let i = 1; i < sections.length; i++) {
            if (sections[i].includes(",")) {
                // Split the section by "," to handle multiple items
                const parts = sections[i].split(",").map((item) => item.trim())
                result[currentKey].push(parts[0])
                currentKey = parts[1] // Update the key to the second part
                result[currentKey] = []
            } else {
                // Add remaining items to the current key
                result[currentKey].push(sections[i].trim())
            }
        }

        return result
    }

    const initialDataSetup = (data) => {
        // ...existing code to set React state...
        setAlertName(data?.alert_name || "")
        setSelectedUsers(
            usersList.filter(
                (user) => data?.users?.includes(user.email) || data?.users?.includes(user.id),
            ),
        )
        setSelectedTime(getSelectedOption(data?.scheduled_at))
        setSelectedTimezone(getSelectedTimezoneOption(data?.timezone))
        setSelectedValues(parseStringToObject(data?.zone))
        setFilterFields(
            data?.filters &&
                data.filters.reduce((acc, cur) => {
                    acc[cur.filter_name] = cur.value
                    return acc
                }, {}),
        )
        setModeOfAlert(data?.mode_of_alert || "email")
        setIsActive(data?.is_active ?? true)
        setUuidForUpdateApi(data?.uuid || "")
    }

    const isAllSelected = selectedUsers?.length === usersList?.length

    const [showAlertLogsDialog, setShowAlertLogsDialog] = useState(false)
    const [alertId, setAlertId] = useState("")
    const [alertNameTitle, setAlertNameTitle] = useState("")

    useEffect(() => {
        const tempTime = []
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const formattedHour = hour % 12 || 12 // Convert 24-hour format to 12-hour format
                const period = hour < 12 ? "AM" : "PM" // Determine AM or PM
                const time = `${formattedHour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${period}`
                const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}:00` // Keep 24-hour format as value
                tempTime.push({ label: time, value })
            }
        }
        setTimeSlots(tempTime)
    }, [])

    // Recursive function to format data for rc-cascader
    const formatOptions = (items) => {
        if (!Array.isArray(items)) return []

        return items
            .map((item) => {
                const children = Array.isArray(item?.value) ? formatOptions(item.value) : []
                const value = item?.key ?? item?.value ?? item?.label
                const label = item?.label ?? item?.key ?? item?.value

                if (
                    value === undefined ||
                    value === null ||
                    label === undefined ||
                    label === null
                ) {
                    return null
                }

                const option = {
                    value: String(value),
                    label: String(label),
                }

                if (children.length > 0) {
                    option.children = children
                }

                return option
            })
            .filter(Boolean)
    }

    // Handle selection change for each cascader
    const handleChange = (filterName, value) => {
        if (value === "" || (Array.isArray(value) && value.length === 0)) {
            let temp = { ...selectedValues }
            delete temp[filterName]
            setSelectedValues({ ...temp })
        } else {
            setSelectedValues((prev) => ({
                ...prev,
                [filterName]: value,
            }))
        }
    }

    const [assetProperty, setAssetProperty] = useState([])
    const [assetOptions, setAssetOptions] = useState([])

    const { values, setValues, errors, touched, handleBlur, setFieldValue, handleSubmit } =
        useFormik({
            initialValues: {
                alertName,
                selectedUsers,
                selectedTime,
                selectedTimezone,
                selectedAsset,
                assetProperty: assetProperty.length ? assetProperty : [],
                selectedValues,
                filterFields,
                // zoneOrFilter: "", // dummy field for custom validation
            },
            validationSchema: AddAlertValidationSchema,
            onSubmit: () => {
                isEditMode ? updateAlert() : addAlert()
            },
            enableReinitialize: true,
        })

    const fetchAssetsOptions = async () => {
        try {
            const resp = await FetchHelper.get("api/v1/asset", {
                only_active: true, //  params instead of query string
            })
            if (resp?.data?.status) {
                setAssetOptions(resp?.data?.data.assets)
            }
        } catch (err) {
            console.log("err--->", err)
        }
    }

    useEffect(() => {
        fetchAssetsOptions()
    }, [])

    const isPropertyVisibleInForm = (prop) =>
        prop?.details?.type === "derived" || Boolean(prop?.details?.visibility?.show_in_form)

    useEffect(() => {
        const fetchAndSetAssetProperty = async () => {
            if (!selectedAsset || selectedAsset === "select-value") {
                setAssetProperty([])
                setFieldValue("assetProperty", [])
                return
            }
            try {
                const resp = await FetchHelper.get("api/v1/asset-property", {
                    asset_uuid: selectedAsset,
                    include_derived_properties: true,
                })
                if (resp?.data?.status) {
                    let assetMeta = resp.data?.data?.asset_property || []
                    let merged = assetMeta
                        .filter(isPropertyVisibleInForm)
                        .map((meta) => ({
                            name: meta.name,
                            details: meta.details,
                            assetValue: "",
                            asset_slug: meta.slug,
                            parent_asset_property_id: null,
                        }))
                    // If editing, merge values from the alert being edited
                    if (isEditMode && editingAlertRef.current) {
                        const filters = editingAlertRef.current.filters || []
                        merged = assetMeta
                            .filter(isPropertyVisibleInForm)
                            .map((meta) => {
                                // Try to find a filter with asset_slug or with a key matching meta.uuid
                                const filter =
                                    filters.find((f) => f.asset_slug === meta.slug) ||
                                    filters.find((f) => f[meta.slug] !== undefined)
                                let assetValue = ""
                                if (filter) {
                                    assetValue =
                                        filter.assetValue ?? filter.value ?? filter[meta.slug] ?? ""
                                }
                                return {
                                    name: meta.name,
                                    details: meta.details,
                                    assetValue,
                                    asset_slug: meta.slug,
                                    parent_asset_property_id: null,
                                }
                            })
                    }
                    setAssetProperty(merged)
                    setFieldValue("assetProperty", merged)
                } else {
                    setAssetProperty([])
                    setFieldValue("assetProperty", [])
                }
            } catch (err) {
                setAssetProperty([])
                setFieldValue("assetProperty", [])
            }
        }
        fetchAndSetAssetProperty()
        // eslint-disable-next-line
    }, [selectedAsset, isEditMode])

    const handleValueChange = (event, el, index, key) => {
        let temp = [...assetProperty]
        if (el?.details?.type === "range") {
            temp[index][key] = event.target.value // This will be an array from Slider
        } else {
            temp[index][key] = event.target.value
        }
        setAssetProperty([...temp])
    }

    useEffect(() => {
        setValues({ ...values, assetProperty: [...assetProperty] })
    }, [assetProperty])

    function valuetext(value) {
        return `${value}%`
    }

    return (
        <div style={{ backgroundColor: "white", height: "100%" }}>
            <Datatable
                filteredResult={data}
                datatableTitle={voiceiiTags("Alerts")}
                columns={columns}
                data={data}
                loading={istableLoading}
                actions={[
                    { name: "Edit", type: "edit", buttonType: "icon" },
                    { name: "History", type: "history", buttonType: "icon" },
                    { name: "Delete", type: "delete", buttonType: "icon" },
                ]}
                addActionName={voiceiiTags("New Alert")}
                columnConfigKey={"alerts_columns_config"}
                addAction={() => {
                    handleFormModalStatus(true)
                    setEditMode(false)
                }}
                refreshAction={() => {
                    fetchAlertsData()
                }}
                onActionClick={async (event, { type, data }) => {
                    if (type === "edit") {
                        const editData = data.values

                        setUuidForUpdateApi(editData?.uuid)
                        setEditMode(true)
                        handleFormModalStatus(true)

                        editingAlertRef.current = editData

                        // First set asset
                        setSelectedAsset(editData.asset_uuid || "select-value")

                        // Initialize non-zone fields
                        initialDataSetup(editData)

                        // Fetch zones first
                        await fetchZonesData(editData.asset_uuid)

                        // THEN set selected zone values
                        const parsedZones = parseStringToObject(editData?.zone)

                        setSelectedValues(parsedZones)

                        setFieldValue("selectedValues", parsedZones)
                    }

                    if (type == "delete") {
                        setDeleteData({
                            uuid: data?.values?.uuid,
                            label: data?.values?.alert_name,
                        })
                        setDeleteModal(true)
                    }
                    if (type == "history") {
                        setAlertId(data?.values?.id)
                        setAlertNameTitle(data?.values?.alert_name)
                        setShowAlertLogsDialog(true)
                    }
                }}
                showMapViewButton={false}
            />

            <Modal
                modalHeading={isEditMode ? voiceiiTags("Update Alert") : voiceiiTags("Add Alert")}
                fullWidth
                maxWidth="md"
                status={formModalStatus}
                onClose={() => {
                    handleFormModalStatus(false)
                    fetchAlertsData()
                    resetState()
                    setSelectedAsset("select-value")
                    setZonesList([])
                }}
                onSave={handleSubmit}
                // disableSaveBtn={!isValid}
            >
                <SimpleBackdropLoader showLoader={isLoading} />

                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            label={voiceiiTags("Alert Name")}
                            fullWidth
                            value={alertName}
                            autoComplete="off"
                            defaultValue={alertName}
                            onChange={(e) => {
                                setAlertName(e.target.value)
                                setFieldValue("alertName", e.target.value)
                            }}
                            error={!!(touched.alertName && errors.alertName)}
                            helperText={touched.alertName && voiceiiTags(errors.alertName)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            id="users-autocomplete"
                            options={[{ id: "selectAll", email: "Select All" }, ...usersList]}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option.email}
                            value={selectedUsers}
                            onChange={(event, newValue) => {
                                handleUserChange(event, newValue)
                                setFieldValue("selectedUsers", newValue)
                            }}
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox
                                        style={{ marginRight: 8 }}
                                        checked={
                                            option.id === "selectAll" ? isAllSelected : selected
                                        }
                                    />
                                    {option.email}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={voiceiiTags("Emails")}
                                    error={!!(touched.selectedUsers && errors.selectedUsers)}
                                    helperText={
                                        touched.selectedUsers && voiceiiTags(errors.selectedUsers)
                                    }
                                />
                            )}
                            renderTags={(tagValue) => {
                                if (tagValue.length === 0) {
                                    return null
                                }
                                const label = `${tagValue[0].email}${
                                    tagValue.length > 1 ? ` (+${tagValue.length - 1} more)` : ""
                                }`
                                return (
                                    <Chip
                                        key={tagValue[0].id}
                                        label={label}
                                        style={{ cursor: "default" }}
                                    />
                                )
                            }}
                            ListboxProps={{
                                style: { maxHeight: "200px" },
                            }}
                        />
                    </Grid>
                </Grid>
                <hr className="alerts-line" />
                <h5 style={{ margin: "1rem 0px" }}>{voiceiiTags("Time & Timezone")}</h5>
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={12}
                        style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                    >
                        <FiClock style={{ fontSize: "5rem", color: "gray" }} />
                        <Autocomplete
                            options={timeSlots}
                            searchable={false}
                            sx={{ width: "100%" }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={voiceiiTags("Time")}
                                    error={!!(touched.selectedTime && errors.selectedTime)}
                                    helperText={
                                        touched.selectedTime && voiceiiTags(errors.selectedTime)
                                    }
                                />
                            )}
                            value={selectedTime}
                            onChange={(e, newValue) => {
                                setSelectedTime(newValue)
                                setFieldValue("selectedTime", newValue)
                            }}
                        />
                        <Autocomplete
                            options={formattedTimezones}
                            sx={{ width: 940 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={voiceiiTags("Timezone")}
                                    error={!!(touched.selectedTimezone && errors.selectedTimezone)}
                                    helperText={
                                        touched.selectedTimezone &&
                                        voiceiiTags(errors.selectedTimezone)
                                    }
                                />
                            )}
                            value={selectedTimezone}
                            onChange={(e, newValue) => {
                                setSelectedTimezone(newValue)
                                setFieldValue("selectedTimezone", newValue)
                            }}
                        />
                    </Grid>
                </Grid>

                <hr className="alerts-line" />
                <h5 style={{ margin: "1rem 0px 2rem 0px" }}>{voiceiiTags("Asset Type")}</h5>
                <Grid container spacing={2}>
                    <Grid
                        item
                        xs={12}
                        // style={{ display: "flex", alignItems: "center", gap: "1rem" }}
                    >
                        <TextField
                            value={selectedAsset}
                            onChange={(event) => setSelectedAsset(event.target.value)}
                            fullWidth
                            select
                            label={voiceiiTags("Select Asset")}
                            name="select_user_role"
                            placeholder={voiceiiTags("Select Asset")}
                            className="text-capitalize"
                            error={!!(touched.selectedAsset && errors.selectedAsset)}
                            helperText={touched.selectedAsset && voiceiiTags(errors.selectedAsset)}
                        >
                            <MenuItem
                                key="select-asset"
                                className="text-capitalize"
                                value="select-value"
                            >
                                {voiceiiTags("Select Asset")}
                            </MenuItem>
                            {assetOptions?.map((item, index) => (
                                <MenuItem
                                    key={index}
                                    className="text-capitalize"
                                    value={item?.uuid}
                                >
                                    {voiceiiTags(formatRoleName(item?.name))}
                                </MenuItem>
                            ))}
                        </TextField>
                        {values?.assetProperty?.length > 0 && (
                            <div
                                style={{
                                    padding: "1.2rem 8px",
                                    margin: "1rem 0",
                                    border:
                                        values?.assetProperty?.length > 0
                                            ? "1px solid lightgray"
                                            : "",
                                    borderRadius: "5px",
                                }}
                                className="w-100"
                            >
                                {values?.assetProperty?.length > 0 && (
                                    <div
                                        style={{
                                            padding: "0px 0px 15px 0px",
                                            borderRadius: "5px",
                                            marginLeft: "0px",
                                        }}
                                    >
                                        {typeof errors.assetProperty === "string" &&
                                            touched.assetProperty && (
                                                <span className="text-error">
                                                    {errors.assetProperty}
                                                </span>
                                            )}
                                    </div>
                                )}
                                {values?.assetProperty?.length > 0 &&
                                    assetProperty
                                        ?.filter(isPropertyVisibleInForm)
                                        ?.map((el, index) => (
                                            <div className="field-content" key={index}>
                                                <TextField
                                                    label={voiceiiTags("Name")}
                                                    name={`assetProperty[${index}].name`}
                                                    disabled
                                                    sx={{
                                                        "& .MuiInputBase-input.Mui-disabled": {
                                                            WebkitTextFillColor: "#000000",
                                                        },
                                                    }}
                                                    value={voiceiiTags(el.name)}
                                                    onChange={(event) =>
                                                        handleValueChange(event, el, index, "name")
                                                    }
                                                    onBlur={handleBlur}
                                                    fullWidth
                                                    placeholder={voiceiiTags("Name")}
                                                    style={{ maxWidth: "25rem", minWidth: "25rem" }}
                                                />

                                                {el?.details?.type != "range" &&
                                                    el?.details?.display_type != "range" &&
                                                    el?.details?.type != "dropdown" && (
                                                        <TextField
                                                            label={voiceiiTags("Asset Value")}
                                                            name={`assetProperty[${index}].assetValue`}
                                                            value={el.assetValue || ""}
                                                            onChange={(event) =>
                                                                handleValueChange(
                                                                    event,
                                                                    el,
                                                                    index,
                                                                    "assetValue",
                                                                )
                                                            }
                                                            onBlur={handleBlur}
                                                            fullWidth
                                                            placeholder={voiceiiTags("Asset Value")}
                                                            style={{
                                                                maxWidth: "25rem",
                                                                minWidth: "25rem",
                                                            }}
                                                        />
                                                    )}
                                                {el?.details?.type === "range" || el?.details?.display_type === "range" && (
                                                    <>
                                                        <div className="d-flex align-items-center">
                                                            <Slider
                                                                getAriaLabel={() => "Asset Value"}
                                                                name={`assetProperty[${index}].assetValue`}
                                                                value={
                                                                    el.assetValue || [
                                                                        el?.details?.validation
                                                                            ?.min_value || 0,
                                                                        el?.details?.validation
                                                                            ?.max_value || 100,
                                                                    ]
                                                                }
                                                                onChange={(_, newValue) =>
                                                                    handleValueChange(
                                                                        {
                                                                            target: {
                                                                                value: newValue,
                                                                            },
                                                                        }, // mimic event
                                                                        el,
                                                                        index,
                                                                        "assetValue",
                                                                    )
                                                                }
                                                                getAriaValueText={valuetext}
                                                                min={
                                                                    el?.details?.validation
                                                                        ?.min_value || 0
                                                                }
                                                                max={
                                                                    el?.details?.validation
                                                                        ?.max_value || 100
                                                                }
                                                                style={{
                                                                    maxWidth: "23rem",
                                                                    minWidth: "23rem",
                                                                    marginLeft: "1rem",
                                                                }}
                                                                disableSwap
                                                                valueLabelDisplay="on"
                                                            />
                                                        </div>
                                                    </>
                                                )}

                                                {el?.details?.type == "dropdown" && (
                                                    <>
                                                        <div
                                                            className="d-flex flex-column"
                                                            style={{
                                                                minWidth: "25rem",
                                                                maxWidth: "25rem",
                                                            }}
                                                        >
                                                            <TextField
                                                                fullWidth
                                                                select
                                                                label={voiceiiTags("Field Type")}
                                                                className="text-capitalize"
                                                                value={el.assetValue || ""}
                                                                onChange={(event) =>
                                                                    handleValueChange(
                                                                        event,
                                                                        el,
                                                                        index,
                                                                        "assetValue",
                                                                    )
                                                                }
                                                                onBlur={handleBlur}
                                                                style={{
                                                                    maxWidth: "25rem",
                                                                    minWidth: "25rem",
                                                                }}
                                                            >
                                                                {el.details.validation?.possible_value?.map(
                                                                    (item, index) => (
                                                                        <MenuItem
                                                                            key={index}
                                                                            className="text-capitalize"
                                                                            value={item}
                                                                        >
                                                                            {formatRoleName(item)}
                                                                        </MenuItem>
                                                                    ),
                                                                )}
                                                            </TextField>
                                                            {el?.details?.validation?.possible_value
                                                                ?.length === 0 && (
                                                                <span className="text-error">
                                                                    Possible options are required.
                                                                </span>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                            </div>
                        )}
                    </Grid>
                </Grid>
                <hr className="alerts-line" />
                <h5 style={{ margin: "1.5rem 0px" }}>{voiceiiTags("Zones")}</h5>

                <Grid container spacing={2}>
                    {zonesList?.map((filter) => {
                        const cascaderOptions = formatOptions(filter?.value)
                        const selectedValue = Array.isArray(selectedValues?.[filter?.label])
                            ? selectedValues[filter.label]
                            : []

                        if (!filter?.label || cascaderOptions.length === 0) {
                            return null
                        }
                        return (
                            <Grid item xs={6} key={filter.label}>
                                <div style={{ marginBottom: "20px" }}>
                                    <Cascader
                                        options={cascaderOptions}
                                        value={selectedValue}
                                        onChange={(value) => {
                                            const normalizedValue = Array.isArray(value)
                                                ? value
                                                : []
                                            handleChange(filter.label, normalizedValue)
                                            const updated = { ...selectedValues }
                                            if (normalizedValue.length > 0) {
                                                updated[filter.label] = normalizedValue
                                            } else {
                                                delete updated[filter.label]
                                            }
                                            setFieldValue("selectedValues", updated)
                                        }}
                                        placeholder={voiceiiTags("Select an option")}
                                    >
                                        <div style={{ position: "relative" }}>
                                            <TextField
                                                style={{
                                                    width: "100%",
                                                    backgroundColor:
                                                        location.pathname == "/reporting"
                                                            ? "#fff"
                                                            : "none",
                                                }}
                                                id="outlined-basic"
                                                label={filter.label}
                                                variant="outlined"
                                                placeholder={voiceiiTags("Select")}
                                                value={
                                                    selectedValue?.length > 0
                                                        ? selectedValue?.join(", ")
                                                        : voiceiiTags("Select Zone")
                                                }
                                                readOnly
                                                autoComplete="off"
                                            />
                                            {selectedValue?.length > 0 && (
                                                <CancelIcon
                                                    style={{
                                                        position: "absolute",
                                                        top: "33%",
                                                        right: "3%",
                                                        cursor: "pointer",
                                                        zIndex: 1000,
                                                        opacity: "0.6",
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleChange(filter.label, "")
                                                        const updated = { ...selectedValues }
                                                        delete updated[filter.label]
                                                        setFieldValue("selectedValues", updated)
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </Cascader>
                                </div>
                            </Grid>
                        )
                    })}
                </Grid>
                {/* Show error for zones/filters */}
                {/* {touched.zoneOrFilter && errors.zoneOrFilter && (
                    <span className="text-error">{errors.zoneOrFilter}</span>
                )} */}

                <hr className="alerts-line" />

                <Grid spacing={2}>
                    <Grid item xs={12}>
                        {isEditMode ? (
                            <RadioGroup
                                row
                                value={isActive.toString()}
                                defaultValue={isActive.toString()}
                                onChange={(e) => setIsActive(e.target.value === "true")}
                            >
                                <FormControlLabel
                                    value="true"
                                    control={<Radio color="primary" />}
                                    label={voiceiiTags("Active")}
                                    labelPlacement="end"
                                />
                                <FormControlLabel
                                    value="false"
                                    control={<Radio color="secondary" />}
                                    label={voiceiiTags("Inactive")}
                                    labelPlacement="end"
                                />
                            </RadioGroup>
                        ) : (
                            ""
                        )}
                    </Grid>
                </Grid>
            </Modal>

            <Modal
                enforceFocus={false}
                fullWidth
                maxWidth="sm"
                onClose={() => {
                    setDeleteModal(false)
                }}
                modalHeading={voiceiiTags("Delete Alert")}
                status={deleteModal}
                onDelete={() => {
                    deleteAlert(deleteData.uuid)
                }}
                deleteButton
                disableSave
            >
                <SimpleBackdropLoader showLoader={isLoading} />
                {`${voiceiiTags("Are you sure you want to delete")} ${deleteData?.label} ${voiceiiTags("Alert")}?`}
            </Modal>

            {/* Alert logs modal  */}
            {alertId && (
                <AlertLogs
                    showAlertLogsDialog={showAlertLogsDialog}
                    setShowAlertLogsDialog={setShowAlertLogsDialog}
                    alertId={alertId}
                    setAlertId={setAlertId}
                    alertNameTitle={alertNameTitle}
                />
            )}
            {/* Alert logs modal  */}
        </div>
    )
}

export default Alerts
